import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query"
import { createTRPCClientProxy, httpLink, loggerLink } from "@trpc/client"
import { createTRPCReact } from "@trpc/react-query"
import superjson from "superjson"

import type { AppRouter } from "@productlane/api"
import { IMPERSONATION_READ_ONLY_MESSAGE } from "@productlane/lib/src/impersonation"

import type { QueryClientConfig } from "@tanstack/react-query"
import type { TRPCClientErrorLike } from "@trpc/client"
import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server"
import { IMPERSONATION_BLOCKED_EVENT } from "@/components/impersonation/impersonation-context"
import { toast } from "@/components/toast"
import { env } from "@/env"
import {
	LOGOUT_DELAY_MS,
	resolveAuthErrorAction,
	WORKSPACE_DELETED_BODY,
	WORKSPACE_DELETED_TITLE,
	WORKSPACE_DELETED_TOAST_ID,
} from "@/lib/unauthorized-redirect"

export const getBaseUrl = () => {
	return `http://localhost:3000`
}

// A screen fires several calls at once, and every one of them fails the same way.
let signOutScheduled = false

const goToLogout = () => {
	window.location.href = "/logout"
}

/** Tears down a session the server no longer accepts. */
function handleAuthError(error: TRPCClientErrorLike<AppRouter>) {
	const action = resolveAuthErrorAction(error, window.location.pathname)
	if (action === "ignore" || signOutScheduled) return
	signOutScheduled = true

	if (action === "workspace-deleted") {
		toast.info({
			id: WORKSPACE_DELETED_TOAST_ID,
			title: WORKSPACE_DELETED_TITLE,
			description: WORKSPACE_DELETED_BODY,
		})
		window.setTimeout(goToLogout, LOGOUT_DELAY_MS)
		return
	}

	goToLogout()
}

const queryClientConfig: QueryClientConfig = {
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: true,
			retry: 0,
		},
	},
	mutationCache: new MutationCache({
		onError: (error) => {
			const typedError = error as TRPCClientErrorLike<AppRouter>
			console.error({
				title: typedError.data?.code ?? "Error",
				description: typedError.message,
			})
			if (typedError.message === IMPERSONATION_READ_ONLY_MESSAGE) {
				window.dispatchEvent(new Event(IMPERSONATION_BLOCKED_EVENT))
			}
			handleAuthError(typedError)
		},
	}),
	queryCache: new QueryCache({
		onError: (error) => {
			console.error("Error happened: ", error)
			const typedError = error as TRPCClientErrorLike<AppRouter>

			handleAuthError(typedError)
		},
	}),
}

export const reactQueryClient = new QueryClient(queryClientConfig)

export const api = createTRPCReact<AppRouter>({})

export const apiClient = api.createClient({
	links: [
		loggerLink({
			enabled: () => import.meta.env.MODE === "development",
		}),
		httpLink({
			url: `${env.VITE_API_URL}/api/trpc`,
			fetch(url, options) {
				return fetch(url, {
					...options,
					credentials: "include",
				})
			},
		}),
	],
	transformer: superjson,
})

// Typed calls outside React, e.g. from the root error boundary above the provider.
export const vanillaApi = createTRPCClientProxy<AppRouter>(apiClient)

/**
 * Inference helper for inputs
 * @example type HelloInput = RouterInputs['example']['hello']
 **/
export type RouterInputs = inferRouterInputs<AppRouter>
/**
 * Inference helper for outputs
 * @example type HelloOutput = RouterOutputs['example']['hello']
 **/
export type RouterOutputs = inferRouterOutputs<AppRouter>
